import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";

/**
 * Live SMS updates over socket.io.
 *
 * The server only has to "poke" the client: each event says something changed
 * and (optionally) which conversation. The client then reloads through the
 * existing REST endpoints, which stay the single source of truth, so the
 * payload shape is deliberately small:
 *
 *   sms:message       { conversation_id, direction }   new inbound / outbound message
 *   sms:status        { conversation_id, status }      delivery status changed
 *   sms:conversation  { conversation_id }              conversation read / renamed / removed
 *
 * Configuration (all optional, build-time):
 *   REACT_APP_SMS_SOCKET=off          disable the socket and keep polling only
 *   REACT_APP_SMS_SOCKET_PATH=/path   socket.io path (default "/socket.io")
 *   REACT_APP_SMS_SOCKET_URL=https://host:port   full origin, if it is not the API host
 */
export const SMS_EVENTS = ["sms:message", "sms:status", "sms:conversation"];

const ENABLED = process.env.REACT_APP_SMS_SOCKET !== "off";
const SOCKET_PATH = process.env.REACT_APP_SMS_SOCKET_PATH || "/socket.io";

// Same host and port as the REST API: "<server>4.my-cloudline.net:9001".
const socketOrigin = (server) => {
  if (process.env.REACT_APP_SMS_SOCKET_URL) return process.env.REACT_APP_SMS_SOCKET_URL;
  const apiHost = String(process.env.REACT_APP_BASE_URL || "").split("/")[0];
  return `${window.location.protocol}//${server}${apiHost}`;
};

// One shared connection for every component that subscribes.
let socket = null;
let socketKey = null;
const eventListeners = new Set();
const statusListeners = new Set();

const notifyStatus = (connected) => statusListeners.forEach((fn) => fn(connected));

const openSocket = (server, token) => {
  socket = io(socketOrigin(server), {
    path: SOCKET_PATH,
    auth: { token },
    transports: ["websocket", "polling"],
    reconnectionDelay: 2000,
    reconnectionDelayMax: 60000,
  });
  socket.on("connect", () => notifyStatus(true));
  socket.on("disconnect", () => notifyStatus(false));
  socket.on("connect_error", () => notifyStatus(false));
  SMS_EVENTS.forEach((event) =>
    socket.on(event, (payload) => eventListeners.forEach((fn) => fn(event, payload || {})))
  );
};

const closeSocket = () => {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
  }
  socket = null;
  socketKey = null;
};

/**
 * Subscribes to SMS events for the given session. Returns an unsubscribe
 * function; the connection closes when the last subscriber leaves.
 */
export const subscribeSmsSocket = ({ server, token }, onEvent, onStatus) => {
  if (!ENABLED || !server || !token) return () => {};

  const key = `${server}|${token}`;
  if (socket && socketKey !== key) closeSocket();
  if (!socket) {
    socketKey = key;
    openSocket(server, token);
  }

  eventListeners.add(onEvent);
  if (onStatus) {
    statusListeners.add(onStatus);
    onStatus(socket.connected);
  }

  return () => {
    eventListeners.delete(onEvent);
    if (onStatus) statusListeners.delete(onStatus);
    if (eventListeners.size === 0) closeSocket();
  };
};

/**
 * React hook: calls onEvent(event, payload) for every SMS event and returns
 * whether the socket is currently connected, so callers can relax polling.
 */
export function useSmsSocket({ server, token }, onEvent) {
  const [connected, setConnected] = useState(false);
  const handler = useRef(onEvent);

  useEffect(() => {
    handler.current = onEvent;
  });

  useEffect(() => {
    const unsubscribe = subscribeSmsSocket(
      { server, token },
      (event, payload) => handler.current(event, payload),
      setConnected
    );
    return () => {
      unsubscribe();
      setConnected(false);
    };
  }, [server, token]);

  return connected;
}
